"use strict";
/**
 * Lease variables (spec 2026-10-15 estate-lease-esign-master §5, contract C3).
 *
 * A lease body is a Markdown subset that may carry `{{name}}` variables:
 *  - plain variables, resolved at send time from the lease/property/business into `mergeData`;
 *  - `{{signature:<key>}}` — the signature block for one signer key;
 *  - `{{signed_date:<key>}}` — the date that signer signed.
 * Signer keys are `resident_1..10`, `guarantor_1..5` and `landlord`.
 *
 * Pure functions only (no runtime dependencies) — safe for Lambda and the browser.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.ESTATE_LEASE_VARIABLES = exports.ESTATE_LEASE_MAX_GUARANTORS = exports.ESTATE_LEASE_MAX_RESIDENTS = void 0;
exports.isKnownLeaseVariable = isKnownLeaseVariable;
exports.isLeaseSignerKey = isLeaseSignerKey;
exports.leaseSignerRole = leaseSignerRole;
exports.leaseVariableRegex = leaseVariableRegex;
exports.parseLeaseVariableToken = parseLeaseVariableToken;
exports.extractLeaseVariables = extractLeaseVariables;
exports.leaseSignerKeys = leaseSignerKeys;
exports.ESTATE_LEASE_MAX_RESIDENTS = 10;
exports.ESTATE_LEASE_MAX_GUARANTORS = 5;
/** The catalogue (spec §5). Order = the Insert-variable menu order. */
exports.ESTATE_LEASE_VARIABLES = [
    // People
    { name: 'landlord_name', group: 'people', label: 'Landlord name' },
    { name: 'landlord_email', group: 'people', label: 'Landlord email' },
    { name: 'residents', group: 'people', label: 'All residents' },
    { name: 'resident_N_name', group: 'people', label: 'Resident N name', indexed: 'resident' },
    { name: 'resident_N_email', group: 'people', label: 'Resident N email', indexed: 'resident' },
    { name: 'guarantors', group: 'people', label: 'All guarantors' },
    { name: 'guarantor_N_name', group: 'people', label: 'Guarantor N name', indexed: 'guarantor' },
    // Property and terms
    { name: 'property_address', group: 'terms', label: 'Property address' },
    { name: 'unit', group: 'terms', label: 'Unit' },
    { name: 'rent', group: 'terms', label: 'Rent' },
    { name: 'deposit', group: 'terms', label: 'Deposit' },
    { name: 'start_date', group: 'terms', label: 'Start date' },
    { name: 'end_date', group: 'terms', label: 'End date' },
    { name: 'due_day', group: 'terms', label: 'Rent due day' },
    { name: 'late_fee', group: 'terms', label: 'Late fee' },
    { name: 'monthly_charges', group: 'terms', label: 'Monthly charges' },
    // Signing blocks
    { name: 'signature:resident_N', group: 'signing', label: 'Resident N signature', indexed: 'resident' },
    { name: 'signature:guarantor_N', group: 'signing', label: 'Guarantor N signature', indexed: 'guarantor' },
    { name: 'signature:landlord', group: 'signing', label: 'Landlord signature' },
    { name: 'signed_date:resident_N', group: 'signing', label: 'Resident N signed date', indexed: 'resident' },
    { name: 'signed_date:guarantor_N', group: 'signing', label: 'Guarantor N signed date', indexed: 'guarantor' },
    { name: 'signed_date:landlord', group: 'signing', label: 'Landlord signed date' },
];
const RESIDENT_IDX = '(10|[1-9])';
const GUARANTOR_IDX = '[1-5]';
const SIGNER_KEY_RE = new RegExp(`^(resident_${RESIDENT_IDX}|guarantor_${GUARANTOR_IDX}|landlord)$`);
const PLAIN_NAMES = new Set(exports.ESTATE_LEASE_VARIABLES.filter((v) => v.group !== 'signing' && !v.indexed).map((v) => v.name));
const INDEXED_PLAIN_RE = new RegExp(`^(resident_${RESIDENT_IDX}_(name|email)|guarantor_${GUARANTOR_IDX}_name)$`);
/** True for a plain (non-signing) catalogue variable, with `N` expanded to a valid index. */
function isKnownLeaseVariable(name) {
    return PLAIN_NAMES.has(name) || INDEXED_PLAIN_RE.test(name);
}
/** True for `resident_1..10`, `guarantor_1..5` or `landlord` (no leading zeros). */
function isLeaseSignerKey(key) {
    return SIGNER_KEY_RE.test(key);
}
/** The role a signer key belongs to, or null for anything that is not a lease signer key. */
function leaseSignerRole(key) {
    if (!isLeaseSignerKey(key))
        return null;
    if (key === 'landlord')
        return 'landlord';
    return key.startsWith('resident_') ? 'resident' : 'guarantor';
}
/**
 * A fresh global regex matching one `{{ … }}` token. Group 1 is the trimmed inner text.
 * Blank tokens (`{{ }}`) and anything containing braces or newlines do not match.
 * Fresh per call because a global regex carries `lastIndex` state.
 */
function leaseVariableRegex() {
    return /\{\{\s*([^{}\s](?:[^{}\n]*?[^{}\s])?)\s*\}\}/g;
}
/** Classify the inner text of one token. `name` is normalised (`signature: resident_1` → `signature:resident_1`). */
function parseLeaseVariableToken(inner) {
    const t = inner.trim();
    const m = /^(signature|signed_date)\s*:\s*(.*)$/.exec(t);
    if (m) {
        const kind = m[1];
        const key = m[2].trim();
        const name = `${kind}:${key}`;
        return isLeaseSignerKey(key) ? { kind, name, key } : { kind: 'unknown', name };
    }
    return isKnownLeaseVariable(t) ? { kind: 'plain', name: t } : { kind: 'unknown', name: t };
}
/** Every variable in a lease body, de-duplicated, in first-seen order per list. */
function extractLeaseVariables(md) {
    const out = { plain: [], signatures: [], signedDates: [], unknown: [] };
    const push = (list, v) => { if (!list.includes(v))
        list.push(v); };
    for (const m of (md ?? '').matchAll(leaseVariableRegex())) {
        const tok = parseLeaseVariableToken(m[1]);
        if (tok.kind === 'plain')
            push(out.plain, tok.name);
        else if (tok.kind === 'signature')
            push(out.signatures, tok.key);
        else if (tok.kind === 'signed_date')
            push(out.signedDates, tok.key);
        else
            push(out.unknown, tok.name);
    }
    return out;
}
/**
 * The signer keys for a lease, in signing-order display: residents, guarantors, then
 * `landlord` (who always countersigns last). Throws RangeError outside 0..10 / 0..5.
 */
function leaseSignerKeys(residentsCount, guarantorsCount) {
    const ok = (n, max) => Number.isInteger(n) && n >= 0 && n <= max;
    if (!ok(residentsCount, exports.ESTATE_LEASE_MAX_RESIDENTS))
        throw new RangeError(`residentsCount must be an integer 0..${exports.ESTATE_LEASE_MAX_RESIDENTS}`);
    if (!ok(guarantorsCount, exports.ESTATE_LEASE_MAX_GUARANTORS))
        throw new RangeError(`guarantorsCount must be an integer 0..${exports.ESTATE_LEASE_MAX_GUARANTORS}`);
    return [
        ...Array.from({ length: residentsCount }, (_, i) => `resident_${i + 1}`),
        ...Array.from({ length: guarantorsCount }, (_, i) => `guarantor_${i + 1}`),
        'landlord',
    ];
}
//# sourceMappingURL=estateLeaseVariables.js.map